import { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useRef, useState } from "react";
import clsx from "clsx";

// Context
import { useManagedSpecialUnits } from "@/Motivators/context/SpecialUnits/ManagedContext";

// Hooks
import { useSelector } from "react-redux";
import { useMutation } from "@tanstack/react-query";
import { useSpecialUnitsInventoryFields } from "@/Motivators/components/SpecialUnits/Tables/hooks/useSpecialUnitsInventoryFields";

// Icons
import { ReactComponent as AssistOn } from "@/assets/icons/actions/AssistOn.svg";
import { ReactComponent as AssistOff } from "@/assets/icons/actions/AssistOff.svg";

// API's
import { searchReynoldsInventory } from "@/Motivators/api/specialUnits";

// MUI
import { Grid, IconButton, Stack, Box, Collapse } from "@mui/material";

// Components
import { CompletionAssistingList } from "@/Layouts/components/InputComponents/InventorySearchInput/CompletionAssistingList";
import { ResultsList } from "./ResultsList";
import PredictiveTextInput from "../PredictiveTextInput";

// Types
import { escapeForRegEx, type Editor } from "@tiptap/core";
import { RootState } from "@/redux/store";
import { ISearchReynoldsInventoryResponse } from "@/Motivators/api/specialUnits/queries";

// Constants
import { baseLexicalCharacters } from "@/Motivators/helpers/specialUnits/InventorySearch/constants";

//Helpers
import { errorLogger } from "@/Common/helpers/logger";
import { tokenize, generateAST, generatePipeline } from "@Common/helpers/Expressions";
import {
	findCursorInfo,
	getCleanKey,
	getCleanValue,
	validateCriteria,
} from "@/Motivators/helpers/specialUnits/InventorySearch/textPositionHelpers";
import {
	checkExistingSearch,
	clearHistoryMap,
	findFirstPrediction,
	generateCompletionListMessage,
	generateResultListMessage,
	getCompleteValue,
	getNormalizedQuery,
	getValueAndCursor,
	prepareAutoCompletion,
} from "@/Motivators/helpers/specialUnits/InventorySearch/helpers";
import { EditorView } from "../PredictiveTextInput/interfaces";

const historySearch = new Map();

interface InventorySearchInputProps {
	rows: Array<{ search: string }>;
	setQuery: Dispatch<SetStateAction<{ input: string; aggregation: string }>>;
	setResponse: Dispatch<SetStateAction<ISearchReynoldsInventoryResponse[]>>;
	dateRange: { from: string; to: string };
}

export default function InventorySearchInput(props: Readonly<InventorySearchInputProps>) {
	const { setHidePanelResponse, setDataSearch, setSearchExist } = useManagedSpecialUnits();
	const locationId = useSelector((state: RootState) => state.location.location?.locationId);

	const [searchResponse, setSearchResponse] = useState<ISearchReynoldsInventoryResponse[]>([]);
	const [loading, setLoading] = useState(false);
	const [prediction, setPrediction] = useState("");
	const [fullPrediction, setFullPrediction] = useState("");
	const [listTitle, setListTitle] = useState("key");
	const [listData, setListData] = useState<string[]>([]);
	const [searchText, setSearchText] = useState("");
	const [error, setError] = useState({ queryError: false, backendError: false });
	const [noDataFound, setNoDataFound] = useState(false);
	const [lockHelp, setLockHelp] = useState(false);
	const [assistedMode, setAssistedMode] = useState(false);
	const HISTORY = 5;

	const editorRef = useRef<Editor | null>(null);

	// new
	const disableActions = !props?.dateRange?.from || !props?.dateRange?.to;

	const { mutate: search } = useMutation({
		mutationFn: (searchCriteria: string) => searchReynoldsInventory(locationId ?? "", searchCriteria),
	});

	const {
		data: { fields, keys, resolvers },
		isLoading,
	} = useSpecialUnitsInventoryFields();

	const lexicalCharacters = useMemo(
		() => [...baseLexicalCharacters, ...keys.map((field) => ({ type: "identifier", symbol: `"${field}"` }))],
		[keys]
	);

	const lastKeyValueRef = useRef<Record<"value" | "key", string | null>>({ value: "", key: null });

	const updateListDataByBracketKey = useCallback(
		(inputValue: string | null, inputKey: string | null) => {
			if (isLoading) {
				return;
			}
			if (lastKeyValueRef.current.value === inputValue && lastKeyValueRef.current.key === inputKey) {
				return;
			}
			lastKeyValueRef.current = { value: inputValue, key: inputKey };

			if (typeof inputValue === "string" && inputKey) {
				setListData([...(fields[inputKey]?.values ?? [])]);
				setListTitle(inputKey);
				if (!inputValue) {
					return;
				}
				const regex = new RegExp(escapeForRegEx(inputValue), "i");
				setListData(fields[inputKey]?.values.filter((value) => regex.test(value)) ?? []);
				return;
			}

			const searchWord = inputKey;
			setListTitle("Keys");
			keys?.length && setListData([...keys]);

			if (searchWord) {
				const regex = new RegExp(escapeForRegEx(searchWord), "i");
				keys?.length && setListData(keys.filter((car) => regex.test(car)));
			}
		},
		[fields, keys, isLoading]
	);

	const toggleAssistedMode = () => {
		setAssistedMode(!assistedMode);
		setLockHelp(!lockHelp);
		if (editorRef.current) {
			handleCursorPositionChange(editorRef.current);
		}
	};

	const handlePrediction = useCallback(
		(value: string, index: number) => {
			if (!editorRef.current) {
				return;
			}
			const cursorInfo = findCursorInfo(index, value);

			const key = getCleanKey(cursorInfo.currentKey);

			const completeValue = getCompleteValue(cursorInfo.hasCloseBracket, cursorInfo.currentValueString);

			const { value: autocompleValue, hasCloseQuote } = getCleanValue(completeValue);

			const whenNotToPredict = [
				!cursorInfo.hasOpenBracket,
				cursorInfo.isBetweenBlocks,
				cursorInfo.autocompleteType === "key" && !cursorInfo.currentKey.startsWith('"'),
				cursorInfo.autocompleteType === "value" && (!cursorInfo.currentValueString?.startsWith('"') || hasCloseQuote),
				index < value.length,
			];

			if (whenNotToPredict.some(Boolean)) {
				setPrediction("");
				setFullPrediction("");
				return;
			}

			if (cursorInfo.autocompleteType === "value") {
				const { rest: restValue, foundPrediction: foundPredictionValue } = findFirstPrediction(
					autocompleValue,
					fields[key]?.values ?? []
				);
				setPrediction(restValue);
				setFullPrediction(foundPredictionValue);
				return;
			}

			const { rest, foundPrediction } = findFirstPrediction(key, keys ?? []);
			setPrediction(rest);
			setFullPrediction(foundPrediction);
		},
		[fields, keys]
	);

	const handleKeyDown = (event: KeyboardEvent) => {
		if (!editorRef.current) {
			return;
		}

		const { currentValue, index } = getValueAndCursor(editorRef.current);
		const cursorInfo = findCursorInfo(index, currentValue);

		if (["Tab", "Enter"].includes(event.key) && !cursorInfo.hasCloseBracket && fullPrediction) {
			handleReplaceString(fullPrediction);
		}
	};

	const handleReplaceString = (text: string) => {
		if (!editorRef.current) {
			return;
		}
		const currentCriteria = editorRef.current.getText();
		const cursorInfo = findCursorInfo(editorRef.current.state.selection.from - 1, currentCriteria);

		let isNumberKeyType = false;
		if (cursorInfo.autocompleteType === "key") {
			isNumberKeyType = fields[text]?.isNumber;
		}

		const completeReplacement = prepareAutoCompletion(text, cursorInfo, isNumberKeyType, currentCriteria);

		const tr = editorRef.current.state.tr;
		tr.insertText(completeReplacement, cursorInfo.replaceStart + 1, cursorInfo.replaceEnd + 1);
		editorRef.current.view.dispatch(tr);
		editorRef.current.view.focus();
		editorRef.current.commands.setTextSelection(cursorInfo.replaceStart + 1 + completeReplacement.length);
	};

	const isAtEnd = useRef(true);

	const handleCursorPositionChange = useCallback(
		(editor: Editor) => {
			const { currentValue, index } = getValueAndCursor(editor);
			const cursorInfo = findCursorInfo(index, currentValue);

			const key = getCleanKey(cursorInfo.currentKey);

			switch (true) {
				case index < currentValue.length && isAtEnd.current:
					isAtEnd.current = false;
					handlePrediction(currentValue, index);
					break;
				case index === currentValue.length && !isAtEnd.current:
					isAtEnd.current = true;
					handlePrediction(currentValue, index);
					break;
				default:
					break;
			}

			if (cursorInfo.autocompleteType === "value") {
				const completeValue = getCompleteValue(cursorInfo.hasCloseBracket, cursorInfo.currentValueString);
				const { value: autocompleValue, hasOpenQuote, hasCloseQuote } = getCleanValue(completeValue);

				if (!hasOpenQuote || hasCloseQuote || cursorInfo.currentValueString === '""') {
					setSearchText("");
					updateListDataByBracketKey("", key);
					return;
				}

				setSearchText(autocompleValue);
				updateListDataByBracketKey(autocompleValue, key);
				return;
			}

			if (cursorInfo.hasCompleteKey) {
				setSearchText("");
				updateListDataByBracketKey(null, "");
				return;
			}

			setSearchText(key);
			updateListDataByBracketKey(null, key);
		},
		[updateListDataByBracketKey, handlePrediction]
	);

	useEffect(() => {
		if (!isLoading && editorRef.current && lastKeyValueRef.current.key === null) {
			lastKeyValueRef.current.key = "";
			handleCursorPositionChange(editorRef.current);
		}
	}, [isLoading, handleCursorPositionChange]);

	const executeOnError = useCallback(() => {
		setSearchResponse([]);
		setError({ queryError: true, backendError: false });
		setLoading(false);
		props?.setResponse([]);
	}, [props?.setResponse]);

	const handleOnChange = (newValue: string) => {
		try {
			if (!newValue || !editorRef.current) {
				setError({ queryError: false, backendError: false });
				setSearchResponse([]);
				setNoDataFound(false);
				updateListDataByBracketKey(null, "");
				return;
			}

			const index = editorRef.current.state.selection.from - 1;

			handlePrediction(newValue, index);

			validateCriteria(newValue);

			const tokens = tokenize(newValue, lexicalCharacters);
			const AST = generateAST(tokens);
			const aggregation = generatePipeline(AST, resolvers);

			const existing = checkExistingSearch(newValue, props.rows);
			setSearchExist(existing);

			setHidePanelResponse(false);
			setError({ queryError: false, backendError: false });

			const normalizedQuery = getNormalizedQuery(tokens);
			if (historySearch.has(normalizedQuery)) {
				const historySearchResponse = historySearch.get(normalizedQuery);
				setSearchResponse(historySearchResponse);
				setDataSearch(historySearchResponse);
				setLoading(false);
				historySearchResponse?.length === 0 ? setNoDataFound(true) : setNoDataFound(false);
				props?.setQuery({ input: normalizedQuery, aggregation });
				props?.setResponse(historySearchResponse);
				return;
			}

			setLoading(true);
			search(JSON.stringify(aggregation), {
				onSuccess: (response) => {
					if (!response) {
						setSearchResponse([]);
						setError({ queryError: false, backendError: true });
						return;
					}
					setSearchResponse(response ?? []);
					setDataSearch(response as SetStateAction<unknown[]>);
					response?.length === 0 ? setNoDataFound(true) : setNoDataFound(false);
					historySearch.set(normalizedQuery, response);
					clearHistoryMap(historySearch, HISTORY);
					props?.setQuery({ input: normalizedQuery, aggregation });
					props?.setResponse(response ?? []);
				},
				onError: () => {
					setSearchResponse([]);
					setError({ queryError: false, backendError: true });
				},
				onSettled: () => {
					setLoading(false);
				},
			});
		} catch (err) {
			executeOnError();
			errorLogger(err as Error);
		}
	};

	const handleTextInput = (_view: EditorView, _from: number, _to: number, text: string) => {
		if (!editorRef.current) {
			return false;
		}

		const { currentValue, index } = getValueAndCursor(editorRef.current);

		const cursorInfo = findCursorInfo(index, currentValue);

		if ("[" === text && !cursorInfo.hasOpenBracket) {
			const tr = editorRef.current.state.tr;
			tr.insertText('["', index + 1, index + 1);
			editorRef.current.view.dispatch(tr);
			return true;
		}

		return false;
	};

	useEffect(() => {
		const fn = () => {
			if (editorRef.current) {
				handleCursorPositionChange(editorRef.current);
			}
		};
		editorRef.current?.on("selectionUpdate", fn);
		return () => {
			editorRef.current?.off("selectionUpdate", fn);
		};
	}, [handleCursorPositionChange]);

	const generatedResultListMessage = generateResultListMessage(error, noDataFound);
	const generatedCompletionListMessage = generateCompletionListMessage(
		searchText,
		Boolean(listData.length),
		listTitle !== "Keys" && fields[listTitle]?.isNumber
	);

	return (
		<Grid container justifyContent="space-between">
			<Grid item>
				<IconButton
					onClick={toggleAssistedMode}
					size="small"
					disabled={disableActions || isLoading}
					className={clsx({ "!text-primary": assistedMode, "!text-default": !assistedMode })}
				>
					{assistedMode ? <AssistOn /> : <AssistOff />}
				</IconButton>
			</Grid>
			<Grid item className="overflow-visible">
				<Stack direction="column" width="830px">
					<PredictiveTextInput
						placeholder={`["Make": "Ford" | "Chevrolet"] and (not ["Status Description": "IN-STOCK"] OR ["Internet Price":>30000])`}
						inputProps={{
							inputClasses: "font-sans",
						}}
						suggestion={prediction}
						error={error.queryError || error.backendError}
						onActionUpdate={(value) => {
							handleOnChange(value ?? "");
						}}
						onActionTextInput={handleTextInput}
						onActionKeyDown={handleKeyDown}
						disabled={disableActions || isLoading}
						editorRef={editorRef}
					/>
					<Box className="relative">
						<Stack
							width="830px"
							direction="row"
							className="absolute top-0 z-10 grid grid-flow-row h-0 overflow-visible"
							alignItems="start"
						>
							<Collapse in={assistedMode} orientation="horizontal" unmountOnExit={false}>
								<Box className="pr-2">
									<CompletionAssistingList
										title={listTitle}
										message={generatedCompletionListMessage}
										values={listData ?? []}
										onClick={(value) => {
											handleReplaceString(value);
										}}
										searchText={searchText}
									/>
								</Box>
							</Collapse>
							<Box className="grow">
								<ResultsList searchResponse={searchResponse} loading={loading} message={generatedResultListMessage} />
							</Box>
						</Stack>
					</Box>
				</Stack>
			</Grid>
		</Grid>
	);
}
